import { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from './useAuth';
import { getPrices } from '../utils/api';

/**
 * Custom hook to determine if a user has premium access to a creator's content.
 * 
 * @param creatorProfileData - The profile data of the creator.
 * @returns { premiumAccess, requiredPremiumKey, isLoading }
 */
export const usePremiumAccess = (creatorProfileData: any) => {
    const { authUser } = useAuth();
    const [premiumAccess, setPremiumAccess] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [requiredPremiumKey, setRequiredPremiumKey] = useState<string>(
        creatorProfileData?.subscription?.custom_product_id || ''
    );

    useEffect(() => {
        const checkPremiumAccess = async () => {
            if (!creatorProfileData) {
                setIsLoading(false);
                return;
            }

            const premiumKey = creatorProfileData.subscription?.custom_product_id;
            setRequiredPremiumKey(premiumKey || '');

            if (!premiumKey) {
                setPremiumAccess(false);
                setIsLoading(false);
                return;
            }

            let access = false;

            // 1. Check if user is the creator
            if (creatorProfileData.userName === authUser?.userName) {
                access = true;
            }
            // 2. Check if creator is Frank Sortino and user is signed in
            else if (
                (creatorProfileData.userName === 'sortino-chat' ||
                    creatorProfileData.userName === 'srinivasjaini') &&
                authUser && !authUser.isAnonymous
            ) {
                access = true;
            }
            // 3. Check if user has purchased premium
            else if (authUser?.premiumPurchases) {
                const currentTimeStamp = Timestamp.fromDate(new Date());
                if (authUser.premiumPurchases[premiumKey]) {
                    // check if not expired
                    if (authUser.premiumPurchases[premiumKey] > currentTimeStamp) {
                        access = true;
                    }
                }
            }

            // 4. Check if price is $0 (if access is still false)
            if (!access) {
                try {
                    const res = await getPrices([premiumKey]);
                    const data = await res.json();
                    if (data && data[premiumKey]) {
                        const priceData = data[premiumKey];
                        if (priceData.unit_amount === 0) {
                            access = true;
                        }
                    }
                } catch (err) {
                    console.error('Error fetching creator price in usePremiumAccess:', err);
                }
            }

            setPremiumAccess(access);
            setIsLoading(false);
        };

        checkPremiumAccess();
    }, [creatorProfileData, authUser]);

    return { premiumAccess, requiredPremiumKey, isLoading };
};
